import React from 'react';
import { User, Calendar, Tag, Trash2, Share2, MessageSquare } from 'lucide-react';
import { EditIcon } from '../icons';
import { formatDate } from '../../utils/dateUtils';

export interface NoteCardData {
  id: string;
  title?: string;
  content: string;
  created_at: string;
  updated_at: string;
  include_in_reports: boolean;
  author_name: string;
  external_author_name?: string;
  tags: string[];
  parent_note_id?: string;
  reply_count?: number;
}

interface NoteCardProps {
  note: NoteCardData;
  onEdit?: (note: NoteCardData) => void;
  onDelete?: (noteId: string) => void;
  onShare?: (noteId: string) => void;
  onReply?: (noteId: string) => void;
  onViewReplies?: (noteId: string) => void;
  showActions?: boolean;
  compact?: boolean;
  isReply?: boolean;
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEdit,
  onDelete,
  onShare,
  onReply,
  onViewReplies,
  showActions = true,
  compact = false,
  isReply = false
}) => {

  return (
    <div className={`card hover:border-primary-300 dark:hover:border-primary-700 transition-colors group ${compact ? 'p-3' : 'p-4'} ${isReply ? 'ml-6 border-l-2 border-l-primary-200 dark:border-l-primary-800' : ''}`}>
      {/* Reply indicator */}
      {isReply && (
        <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" />
          <span>Reply</span>
        </div>
      )}
      
      {/* Note Header with Actions */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 text-left">
          {/* Note Title */}
          {note.title && (
            <h4 className={`font-medium text-text-primary dark:text-text-dark-primary text-left ${compact ? 'text-sm' : 'text-base'}`}>
              {note.title}
            </h4>
          )}
        </div>
        
        {/* Action Icons */}
        {showActions && (onEdit || onDelete || onShare || onReply) && (
          <div className="flex items-center gap-2 ml-2">
            {onReply && !isReply && (
              <button
                onClick={() => onReply(note.id)}
                className="opacity-0 group-hover:opacity-100 p-2 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 transition-all rounded"
                title="Reply to note"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            )}
            {onShare && !isReply && (
              <button
                onClick={() => onShare(note.id)}
                className="opacity-0 group-hover:opacity-100 p-2 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 transition-all rounded"
                title="Share for feedback"
              >
                <Share2 className="w-4 h-4" />
              </button>
            )}
            {onEdit && (
              <button
                onClick={() => onEdit(note)}
                className="opacity-0 group-hover:opacity-100 p-2 text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-600 dark:hover:text-primary-400 transition-all rounded"
                title="Edit note"
              >
                <EditIcon className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(note.id)}
                className="opacity-0 group-hover:opacity-100 p-2 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-500 dark:hover:text-error-400 transition-all rounded"
                title="Delete note"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Note Content */}
      <div className={`text-text-secondary dark:text-text-dark-secondary mb-3 leading-relaxed text-left ${compact ? 'text-xs' : 'text-sm'}`}>
        <div dangerouslySetInnerHTML={{ __html: note.content }} />
      </div>

      {/* Note Metadata */}
      <div className={`flex items-center justify-between text-text-tertiary dark:text-text-dark-tertiary ${compact ? 'text-xs' : 'text-xs'}`}>
        <div className="flex items-center space-x-4">
          {/* Author */}
          <div className="flex items-center">
            <User className="w-3 h-3 mr-1" />
            <span>{note.author_name}</span>
          </div>

          {/* Date */}
          <div className="flex items-center">
            <Calendar className="w-3 h-3 mr-1" />
            <span>{formatDate(note.updated_at)}</span>
          </div>

          {/* Reply Count */}
          {!isReply && note.reply_count && note.reply_count > 0 && (
            <button
              onClick={() => onViewReplies?.(note.id)}
              className="flex items-center text-primary-600 dark:text-primary-400 hover:underline"
            >
              <MessageSquare className="w-3 h-3 mr-1" />
              <span>{note.reply_count} {note.reply_count === 1 ? 'reply' : 'replies'}</span>
            </button>
          )}
        </div>

        {/* Include in Reports Indicator */}
        {note.include_in_reports && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-success-500 rounded-full" title="Included in reports" />
            <span className="text-xs text-success-600 dark:text-success-400">In Reports</span>
          </div>
        )}
      </div>

      {/* Tags */}
      {note.tags && note.tags.length > 0 && (
        <div className="flex items-center mt-3">
          <Tag className="w-3 h-3 mr-2 text-text-tertiary dark:text-text-dark-tertiary" />
          <div className="flex flex-wrap gap-2">
            {note.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteCard;


